import type { Component } from 'vue'
import {
  Dices,
  Rocket,
  Bomb,
  Target,
  FerrisWheel,
  Gamepad2,
  Spade,
  Club,
  Gem,
  Crown,
  Trophy,
  Medal,
  Star,
  Gift,
  Bell,
  BellRing,
  Search,
  Wallet,
  CreditCard,
  Banknote,
  DollarSign,
  TrendingUp,
  BarChart3,
  Hourglass,
  UserPlus,
  User,
  Users,
  Settings,
  LogOut,
  Eye,
  Ban,
  OctagonX,
  Download,
  Handshake,
  CircleCheckBig,
  Volume2,
  VolumeX,
  ShieldCheck,
  AlertTriangle,
  CircleCheck,
  CircleX,
  Info,
  Package,
  Send,
  Lock,
  LayoutDashboard,
  Smartphone,
  Joystick,
  Image as ImageIcon,
  Sparkles,
  CircleDot,
  Home,
  Clock,
  ClipboardList,
  FileText,
  Zap,
  MoreHorizontal,
  Flame,
  Layers,
  Globe,
} from 'lucide-vue-next'

export interface IconEntry {
  emoji: string
  lucide: Component
}

const registry: Record<string, IconEntry> = {
  // ── Game categories ──
  'category-slots': { emoji: '\uD83C\uDFB0', lucide: Joystick },
  'category-table': { emoji: '\uD83C\uDCCF', lucide: Club },
  'category-instant': { emoji: '\uD83D\uDE80', lucide: Rocket },
  'category-video-poker': { emoji: '\uD83C\uDCCF', lucide: Spade },
  'category-default': { emoji: '\uD83C\uDFB2', lucide: Dices },

  // ── Game icons ──
  'game-crash': { emoji: '\uD83D\uDE80', lucide: Rocket },
  'game-dice': { emoji: '\uD83C\uDFB2', lucide: Dices },
  'game-plinko': { emoji: '\uD83D\uDC8E', lucide: Gem },
  'game-mines': { emoji: '\uD83D\uDCA3', lucide: Bomb },
  'game-slots': { emoji: '\uD83C\uDFB0', lucide: Joystick },
  'game-hilo': { emoji: '\uD83C\uDCCF', lucide: Club },
  'game-keno': { emoji: '\uD83C\uDFAF', lucide: Target },
  'game-wheel': { emoji: '\uD83C\uDFA1', lucide: FerrisWheel },
  'game-blackjack': { emoji: '\u2660\uFE0F', lucide: Spade },
  'game-roulette': { emoji: '\uD83C\uDFA1', lucide: CircleDot },
  'game-video-poker': { emoji: '\uD83C\uDCCF', lucide: Spade },
  'game-baccarat': { emoji: '\uD83C\uDCCF', lucide: Club },
  'game-default': { emoji: '\uD83C\uDFAE', lucide: Gamepad2 },

  // ── Notification types ──
  'notif-deposit': { emoji: '\uD83D\uDCB0', lucide: Wallet },
  'notif-withdrawal': { emoji: '\uD83D\uDCB8', lucide: Send },
  'notif-bonus': { emoji: '\uD83C\uDF81', lucide: Gift },
  'notif-vip': { emoji: '\u2B50', lucide: Star },
  'notif-system': { emoji: '\uD83D\uDD14', lucide: Bell },
  'notif-win': { emoji: '\uD83C\uDFC6', lucide: Trophy },
  'notif-security': { emoji: '\uD83D\uDD10', lucide: Lock },
  'notif-leaderboard': { emoji: '\uD83D\uDCCA', lucide: BarChart3 },

  // ── VIP tiers ──
  'vip-bronze': { emoji: '\uD83E\uDD49', lucide: Medal },
  'vip-silver': { emoji: '\uD83E\uDD48', lucide: Medal },
  'vip-gold': { emoji: '\uD83E\uDD47', lucide: Medal },
  'vip-diamond': { emoji: '\uD83D\uDC8E', lucide: Gem },
  'vip-crown': { emoji: '\uD83D\uDC51', lucide: Crown },

  // ── Leaderboard podium ──
  'podium-1st': { emoji: '\uD83D\uDC51', lucide: Crown },
  'podium-2nd': { emoji: '\uD83E\uDD48', lucide: Medal },
  'podium-3rd': { emoji: '\uD83E\uDD49', lucide: Medal },

  // ── Navigation / UI ──
  'nav-gem': { emoji: '\uD83D\uDC8E', lucide: Gem },
  'nav-search': { emoji: '\uD83D\uDD0D', lucide: Search },
  'nav-balance': { emoji: '\uD83D\uDCB0', lucide: Wallet },
  'nav-bell': { emoji: '\uD83D\uDD14', lucide: Bell },
  'nav-bell-ring': { emoji: '\uD83D\uDD14', lucide: BellRing },
  'nav-user': { emoji: '\uD83D\uDC64', lucide: User },
  'nav-settings': { emoji: '\u2699\uFE0F', lucide: Settings },
  'nav-wallet': { emoji: '\uD83D\uDCB3', lucide: CreditCard },
  'nav-logout': { emoji: '\uD83D\uDEAA', lucide: LogOut },
  'nav-dashboard': { emoji: '\uD83D\uDCCA', lucide: LayoutDashboard },

  // ── Admin dashboard stats ──
  'stat-revenue': { emoji: '\uD83D\uDCB0', lucide: DollarSign },
  'stat-players': { emoji: '\uD83D\uDC65', lucide: Users },
  'stat-deposits': { emoji: '\uD83D\uDCB3', lucide: CreditCard },
  'stat-withdrawals': { emoji: '\uD83D\uDCE4', lucide: Send },
  'stat-ggr': { emoji: '\uD83D\uDCC8', lucide: TrendingUp },
  'stat-pending': { emoji: '\u23F3', lucide: Hourglass },
  'stat-new-users': { emoji: '\uD83C\uDD95', lucide: UserPlus },
  'stat-bonuses': { emoji: '\uD83C\uDF81', lucide: Gift },
  'stat-claims': { emoji: '\uD83D\uDCE6', lucide: Package },
  'stat-claim-rate': { emoji: '\uD83D\uDCCA', lucide: BarChart3 },
  'stat-liability': { emoji: '\uD83D\uDCB8', lucide: Banknote },

  // ── Admin activity ──
  'activity-registration': { emoji: '\uD83D\uDC64', lucide: UserPlus },
  'activity-large-deposit': { emoji: '\uD83D\uDCB3', lucide: CreditCard },
  'activity-large-win': { emoji: '\u2B50', lucide: Star },
  'activity-withdrawal-request': { emoji: '\uD83D\uDCE4', lucide: Send },

  // ── Admin player actions ──
  'action-view': { emoji: '\uD83D\uDC41\uFE0F', lucide: Eye },
  'action-ban': { emoji: '\uD83D\uDEAB', lucide: Ban },
  'action-suspend': { emoji: '\u26D4', lucide: OctagonX },
  'action-export': { emoji: '\uD83D\uDCE5', lucide: Download },

  // ── Affiliate stats ──
  'affiliate-partners': { emoji: '\uD83E\uDD1D', lucide: Handshake },
  'affiliate-verified': { emoji: '\u2705', lucide: CircleCheckBig },
  'affiliate-commission': { emoji: '\uD83D\uDCB5', lucide: Banknote },
  'affiliate-referrals': { emoji: '\uD83D\uDC65', lucide: Users },

  // ── Game controls ──
  'sound-on': { emoji: '\uD83D\uDD0A', lucide: Volume2 },
  'sound-off': { emoji: '\uD83D\uDD07', lucide: VolumeX },
  'provably-fair': { emoji: '\uD83D\uDD12', lucide: ShieldCheck },
  'cashout': { emoji: '\uD83D\uDCB0', lucide: Wallet },

  // ── Toast icons ──
  'toast-success': { emoji: '\u2714\uFE0F', lucide: CircleCheck },
  'toast-error': { emoji: '\u2716\uFE0F', lucide: CircleX },
  'toast-warning': { emoji: '\u26A0\uFE0F', lucide: AlertTriangle },
  'toast-info': { emoji: '\u2139\uFE0F', lucide: Info },

  // ── Extra navigation ──
  'nav-home': { emoji: '\uD83C\uDFE0', lucide: Home },
  'nav-history': { emoji: '\uD83D\uDD52', lucide: Clock },
  'nav-casino': { emoji: '\uD83C\uDFB2', lucide: Dices },
  'nav-games': { emoji: '\uD83C\uDFAE', lucide: Gamepad2 },
  'nav-audit': { emoji: '\uD83D\uDCCB', lucide: ClipboardList },
  'nav-content': { emoji: '\uD83D\uDCC4', lucide: FileText },
  'nav-more': { emoji: '\u2022\u2022\u2022', lucide: MoreHorizontal },
  'nav-promotions': { emoji: '\uD83C\uDF81', lucide: Gift },
  'nav-vip': { emoji: '\uD83C\uDFC6', lucide: Trophy },
  'nav-leaderboard': { emoji: '\uD83D\uDCCA', lucide: BarChart3 },
  'nav-affiliates': { emoji: '\uD83E\uDD1D', lucide: Handshake },
  'nav-finance': { emoji: '\uD83D\uDCB0', lucide: DollarSign },
  'nav-bonuses': { emoji: '\uD83C\uDF81', lucide: Gift },
  'nav-players': { emoji: '\uD83D\uDC65', lucide: Users },
  'nav-rbac': { emoji: '\uD83D\uDC64', lucide: User },
  'nav-jurisdiction': { emoji: '\uD83C\uDF0D', lucide: Globe },
  'category-crash': { emoji: '\uD83D\uDE80', lucide: Rocket },
  'category-originals': { emoji: '\uD83C\uDFAF', lucide: Zap },
  'game-baccarat-card': { emoji: '\uD83C\uDCCF', lucide: Layers },
  'bust': { emoji: '\uD83D\uDCA5', lucide: Flame },

  // ── Misc ──
  'warning': { emoji: '\u26A0\uFE0F', lucide: AlertTriangle },
  'empty-state-game': { emoji: '\uD83C\uDFAE', lucide: Gamepad2 },
  'empty-state-bonus': { emoji: '\uD83C\uDF81', lucide: Gift },
  'empty-state-bell': { emoji: '\uD83D\uDD14', lucide: Bell },
  'empty-state-trophy': { emoji: '\uD83C\uDFC6', lucide: Trophy },
  'mobile-rotate': { emoji: '\uD83D\uDCF1', lucide: Smartphone },
  'logo-placeholder': { emoji: '\uD83D\uDDBC', lucide: ImageIcon },
  'safe-gem': { emoji: '\uD83D\uDC8E', lucide: Gem },
  'sparkles': { emoji: '\u2728', lucide: Sparkles },
}

export function getIconEntry(name: string): IconEntry | null {
  return registry[name] ?? null
}

export default registry
